"use client";
import { ItemList } from "@/components/itemList/itemList";
import { OrderType } from "@/constants/enum";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import pom from "@/utils/pom";
import { LocalStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { usePathname } from "next/navigation";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export interface ItemLoadingProps {
  key?: string;
  className?: string;
  containerClassName?: string;
  shouldShowText?: boolean;
}

export const CategoryPageSkeleton = ({
  itemTemplate = "image focused",
}: {
  itemTemplate?: string;
}) => {
  return (
    <div className="flex-1 flex flex-col px-6 sm:px-12 pb-12 pt-6">
      <div className="flex flex-col sm:flex-row w-full justify-between gap-4 sm:items-center items-start mb-12">
        <Skeleton
          className="h-full"
          containerClassName="flex items-center justify-center font-semibold text-[30px] leading-[45px] h-[45px] w-[250px]"
          inline={true}
        />
        <Skeleton
          className="h-full"
          containerClassName="flex items-center justify-center h-[48px] w-[200px]"
          inline={true}
        />
      </div>
      <ItemList listType={itemTemplate}>
        {itemTemplate.toLowerCase() === "image focused" &&
          Array.from({ length: 8 }, () => null).map((_, index) => (
            <ItemLoading
              key={`image-focused-shimmer-${index}`}
              className="relative pb-[133.33%] h-0 leading-0"
              containerClassName=""
              shouldShowText={true}
            />
          ))}
        {itemTemplate.toLowerCase() === "description focused" &&
          Array.from({ length: 4 }, () => null).map((_, index) => (
            <ItemLoading
              key={`description-focused-shimmer-${index}`}
              className="relative"
              containerClassName="flex relative h-[120px] shadow-md rounded-b-md bg-white"
              shouldShowText={false}
            />
          ))}
      </ItemList>
      <div className="mt-6 mx-auto flex flex-col items-center justify-between h-[70.5px]">
        <Skeleton
          containerClassName={`w-2/5 min-w-[160px] py-2 mb-2`}
          inline={true}
        />
        <Skeleton
          containerClassName={`w-1/4 min-w-[160px] h-[22.5px]`}
          inline={true}
        />
      </div>
    </div>
  );
};

export const ItemLoading = ({
  className,
  containerClassName,
  shouldShowText,
}: ItemLoadingProps) => {
  return (
    <div className="flex flex-col gap-5">
      <Skeleton
        className={className}
        containerClassName={`leading-[0] ${containerClassName}`}
        inline={true}
      />
      {shouldShowText && (
        <div className="flex flex-col gap-2">
          <Skeleton
            className="h-4"
            containerClassName="w-3/4 h-6 gap-4"
            inline={true}
          />
          <Skeleton
            className="h-4"
            containerClassName="w-1/2 h-6 gap-4"
            inline={true}
          />
        </div>
      )}
    </div>
  );
};

export const ItemPageSkeleton = () => {
  return (
    <div
      className="flex flex-col sm:flex-row justify-center sm:gap-20 px-8 pt-6 w-full h-full"
      data-testid={applyDataTestId(pom.landingPage.itemPageSkeleton)}
    >
      <div className="flex-shrink-0 w-full sm:w-[420px]">
        <Skeleton
          className="h-0 mb-8 pb-[133.33%]"
          containerClassName="relative overflow-hidden rounded-md bg-gray-200 mb-8 leading-[0]"
        />
      </div>
      <div className="sm:w-[40%]">
        <Skeleton
          className="w-full mb-4 font-bold break-words"
          containerClassName="flex w-1/2 min-w-[120px] text-4xl"
        />
        <Skeleton
          className="font-semibold mb-6"
          containerClassName="flex w-1/3 min-w-[80px] text-[28px]"
        />
        <Skeleton
          inline={true}
          count={8}
          className="text-base"
          containerClassName="flex flex-col justify-around mb-4 gap-4"
          style={{ lineHeight: "1.5rem" }}
        />
      </div>
    </div>
  );
};

export const GiftCardSkeleton = () => {
  return (
    <div className="flex-1 flex flex-col container w-full mx-auto">
      <div className="flex sm:flex-row flex-col-reverse pt-8 sm:gap-20 gap-5 pb-10 p-8">
        <div className="flex flex-col gap-6 flex-1">
          <Skeleton
            className="h-full"
            containerClassName="w-[300px] h-[45px] mb-4"
            inline={true}
          />
          <Skeleton
            className="h-full"
            containerClassName="min-w-[300px] w-[70%] h-[18px]"
            inline={true}
          />
          <Skeleton
            className="h-full"
            containerClassName="min-w-[300px] w-[70%] h-[18px]"
            inline={true}
          />
          <Skeleton
            className="h-full"
            containerClassName="min-w-[200px] w-[55%] h-[18px]"
            inline={true}
          />
          <Skeleton
            className="h-full"
            containerClassName="min-w-[200px] w-[55%] h-[18px]"
            inline={true}
          />
          <Skeleton
            className="h-full"
            containerClassName="min-w-[150px] w-[45%] h-[18px]"
            inline={true}
          />
          <Skeleton
            className="h-full"
            containerClassName="min-w-[150px] w-[45%] h-[18px]"
            inline={true}
          />
          <Skeleton
            className="rounded-md py-2 px-2 sm:px-3 md:px-8 2xl:w-[696px] xl:w-[568px] lg:w-[440px] md:w-[312px] w-full h-full mt-4"
            containerClassName="sm:hidden flex w-full h-[56px]"
            inline={true}
            style={{ borderRadius: ".375rem" }}
          />
        </div>
        <div className="flex-1">
          <Skeleton
            className="h-full"
            containerClassName="2xl:w-[696px] 2xl:h-[465.5px] xl:w-[568px] xl:h-[380px] lg:w-[440px] lg:h-[294px] md:w-[312px] md:h-[209px] w-full h-auto aspect-[1.5] block"
            inline={true}
            style={{ borderRadius: "1.5rem" }}
          />
          <Skeleton
            className="rounded-md py-2 px-2 sm:px-3 md:px-8 2xl:w-[696px] xl:w-[568px] lg:w-[440px] md:w-[312px] w-full h-full sm:mt-8"
            containerClassName="hidden sm:flex w-full h-[56px]"
            inline={true}
            style={{ borderRadius: ".375rem" }}
          />
        </div>
      </div>
    </div>
  );
};

export const AboutPageSkeleton = () => {
  return (
    <div className="w-full flex flex-col sm:flex-row px-4 sm:px-14 pb-10 pt-6 gap-14 items-center justify-center">
      <Skeleton
        className="rounded-lg sm:min-h-[400px] h-[100px]"
        containerClassName="basis-1/2 flex justify-center w-[150px] sm:w-full"
        inline={true}
        style={{ borderRadius: ".5rem" }}
      />
      <div className="w-full basis-1/2 flex flex-col gap-6">
        <Skeleton
          className="w-full h-[40px] text-[40px]"
          containerClassName="w-[200px] flex justify-center"
          inline={true}
        />
        <Skeleton
          className="w-full h-[15px]"
          containerClassName="w-full gap-4 flex flex-col justify-center"
          count={5}
          inline={true}
        />
      </div>
    </div>
  );
};

export const ServiceTimeSkeleton = () => {
  return (
    <div className="grid grid-cols-3 gap-4">
      {Array.from({ length: 15 }, () => null).map((_, index) => {
        return (
          <Skeleton
            key={`shimmer-timeslots-${index}`}
            className="text-[14px] font-medium w-full h-full py-2 px-4"
            containerClassName="h-[44px] rounded-md w-full"
            inline={true}
          />
        );
      })}
    </div>
  );
};

const SingleMenuItemsSkeleton = ({
  itemTemplate,
}: {
  itemTemplate?: string;
}) => {
  return (
    <div data-testid={applyDataTestId(pom.skeletons.allSkeletons)}>
      <Skeleton
        className="text-[15px]"
        containerClassName="flex w-1/6 min-w-[150px] font-medium h-[22.5px] mb-5 mt-8"
      />
      <ItemList listType={itemTemplate?.toLowerCase()}>
        {itemTemplate?.toLowerCase() === "image focused" &&
          Array.from({ length: 20 }, () => null).map((_, index) => (
            <ItemLoading
              key={`image-focused-shimmer-${index}`}
              className="relative pb-[133.33%] h-0 leading-0"
              shouldShowText={true}
            />
          ))}
        {itemTemplate?.toLowerCase() === "description focused" &&
          Array.from({ length: 10 }, () => null).map((_, index) => (
            <ItemLoading
              key={`description-focused-shimmer-${index}`}
              className="relative"
              containerClassName="flex relative h-[120px] shadow-md rounded-b-md bg-white"
              shouldShowText={false}
            />
          ))}
      </ItemList>
    </div>
  );
};

export const MenuItemsSkeleton = ({
  itemTemplate = "description focused",
}: {
  itemTemplate?: string;
}) => {
  return (
    <>
      <SingleMenuItemsSkeleton itemTemplate={itemTemplate} />
      <SingleMenuItemsSkeleton itemTemplate={itemTemplate} />
    </>
  );
};

export const HeaderSkeleton = () => {
  const pathName = usePathname();
  const tableData = getFromLocalStorage(LocalStorageKey.TABLE_DATA, undefined);
  const scanToOrderInfo = getFromLocalStorage(
    LocalStorageKey.SCAN_TO_ORDER_INFO,
    {
      tableId: "",
      employeeId: "",
      orderType: OrderType.PICKUP as OrderType,
    }
  );
  const isScanToOrder = tableData && scanToOrderInfo;
  const showMenu =
    pathName !== "/customer-profile" &&
    pathName !== "/verify-email" &&
    pathName !== "/checkout" &&
    pathName !== "/guest-checkout" &&
    pathName !== "/confirmation";
  const notShowHeader = pathName == "/confirmation" && isScanToOrder;
  if (notShowHeader) return null;
  return (
    <nav
      aria-label="skeleton navigation bar"
      className={[
        "flex",
        "flex-col",
        "items-center",
        "relative",
        "top-0",
        "z-10",
        "bg-[#FBFBFB]",
      ].join(" ")}
    >
      <div
        className={[
          "flex",
          "flex-row",
          "px-5",
          "pt-5",
          "w-full",
          "justify-between",
        ].join(" ")}
      >
        <div
          className={["flex", "flex-row", "sm:gap-6", "items-center"].join(" ")}
        >
          <Skeleton
            className="sm:block h-[51px] w-[51px] sm:h-16 sm:w-16 rounded-none"
            containerClassName="hidden sm:block h-[51px] w-[51px] sm:h-16 sm:w-16"
            style={{ borderRadius: "0" }}
          />
          <Skeleton
            className="text-2xl sm:text-[32px] leading-normal font-normal sm:h-auto h-[33px]"
            containerClassName="flex flex-col w-[150px] justify-center items-center"
            inline={true}
          />
        </div>
        <div className="flex flex-col gap-6 items-center justify-center sm:w-[136px] w-[80px]">
          <Skeleton
            className="text-sm flex items-center w-full h-full"
            containerClassName="rounded text-sm flex items-center w-full sm:h-[36px] h-[32px]"
            inline={true}
          />
        </div>
      </div>
      {showMenu && (
        <div className="w-full">
          <Skeleton
            className="sm:h-[42px] w-full h-[20px]"
            containerClassName="w-full px-5 sm:h-[62px] h-[72px] py-6 sm:py-0 mt-0 flex flex-col justify-end"
            inline={true}
          />
        </div>
      )}
    </nav>
  );
};

export const NavBarSkeleton = () => {
  return (
    <div
      className="w-full"
      data-testid={applyDataTestId(pom.skeletons.navBarSkeleton)}
    >
      <Skeleton
        className="sm:h-[42px] w-full h-[20px]"
        containerClassName="w-full px-5 sm:h-[62px] h-[72px] py-6 sm:py-0 mt-0 flex flex-col justify-end"
        inline={true}
      />
    </div>
  );
};

export const HeroSkeleton = () => {
  return (
    <>
      {/* <Skeleton
        className="w-[275px]"
        containerClassName="pt-4 pr-0 text-center sm:text-right sm:pr-8"
        style={{ width: "275px" }}
      /> */}
      <Skeleton
        className="h-[500px] sm:rounded-lg"
        containerClassName="w-full flex flex-col items-center pt-6 sm:px-8"
        inline={true}
      />
    </>
  );
};

export const ProductsSkeleton = ({
  itemTemplate = "image focused",
}: {
  itemTemplate: string;
}) => {
  return (
    <div
      className={[
        "bg-white",
        "p-6",
        "my-6",
        "rounded-lg",
        "mx-8",
        `${
          itemTemplate.toLowerCase() == "image focused" &&
          "border border-gray-300"
        }`,
      ].join(" ")}
    >
      <div className="flex flex-col justify-between sm:flex-row gap-6 mb-8">
        <Skeleton
          className="w-full h-full"
          containerClassName="flex font-semibold text-[30px] leading-[45px] w-[200px] h-[45px]"
        />
        <Skeleton
          className="w-full h-full justify-center px-3 py-2.5 rounded-lg font-medium text-sm leading-[21px]"
          containerClassName="flex flex-row sm:w-[148px] w-full items-center h-[43px]"
          inline={true}
        />
      </div>
      <div className="mt-6">
        <ItemList listType={itemTemplate.toLowerCase()}>
          {itemTemplate.toLowerCase() === "image focused" &&
            Array.from({ length: 8 }, () => null).map((_, index) => (
              <ItemLoading
                key={`image-focused-shimmer-${index}`}
                className="relative pb-[133.33%] h-0 leading-0"
                shouldShowText={true}
              />
            ))}
          {itemTemplate.toLowerCase() === "description focused" &&
            Array.from({ length: 4 }, () => null).map((_, index) => (
              <ItemLoading
                key={`description-focused-shimmer-${index}`}
                className="relative"
                containerClassName="flex relative h-[120px] shadow-md rounded-b-md bg-white"
                shouldShowText={false}
              />
            ))}
        </ItemList>
      </div>
    </div>
  );
};

export const FooterSkeleton = ({ count }: { count: number }) => {
  return (
    <div className="flex flex-col container w-full mx-auto">
      <div className="text-[15px] justify-between flex flex-wrap gap-[57px] px-12 w-full items-start max-md:px-0 max-md:flex-col py-[60px] bg-[#F8F8F8]">
        <Skeleton
          containerClassName={`flex flex-col w-[240px] max-md:ml-0 max-md:w-full max-md:px-5 mr-2 gap-4 justify-start items-start`}
          count={count}
          inline={true}
        />
        <Skeleton
          className={`pb-[100%] h-0 w-[280px]`}
          containerClassName={`w-[280px] h-[280px] max-md:ml-0 max-md:w-full flex justify-center overflow-hidden`}
          style={{ width: "280px" }}
        />
        <Skeleton
          containerClassName={`flex flex-col justify-center items-center w-[312px] max-md:ml-0 max-md:w-full max-md:px-5 flex gap-4 justify-start items-start`}
          count={count}
          inline={true}
        />
        <Skeleton
          containerClassName={`w-[145.08px] max-md:ml-0 max-md:w-full justify-center max-md:px-5 mr-2 flex flex-col gap-4 justify-start`}
          count={count}
          inline={true}
        />
      </div>
    </div>
  );
};

export const IconSkeleton = ({
  width,
  height,
  styles,
}: {
  width: number;
  height: number;
  styles?: {};
}) => {
  return (
    <div
      className={`flex justify-center items-center w-[${width}px] h-[${height}px]`}
      data-testid={applyDataTestId(pom.skeletons.iconSkeleton)}
    >
      <Skeleton
        containerClassName="w-full h-full flex"
        width={width}
        height={height}
        style={{ ...styles }}
      />
    </div>
  );
};

export const RecentOrderedItemsSkeleton = ({ count = 3 }: { count?: number }) => (
  <div className="container mx-auto px-4 sm:px-8 pt-4 flex flex-col">
    <Skeleton className="h-full" containerClassName="flex w-[80px] h-[16px] mb-2" inline />
    {Array.from({ length: count }, (_, index) => (
      <Skeleton
        key={`recent-order-skeleton-${index}`}
        className="h-full"
        containerClassName="flex w-full h-[44px] mb-1.5"
        inline
        style={{ borderRadius: '0.375rem' }}
      />
    ))}
  </div>
);
